/**
 * Selecting which caught cancellation dates to show for an outing.
 *
 * Seasonal outings (those with a `peakSeason` config from the backend registry)
 * should surface "Peak season dates" rather than generic upcoming dates: in the
 * off-season the next upcoming date can be months away or absent, which reads as
 * "nothing happens here". Scoping to the peak-season window keeps the evidence
 * relevant. A fallback chain keeps the section populated when the strict window
 * is empty.
 *
 * `peakSeason` shape (from the backend): { startMonth, endMonth, label } where
 * months are 1-based and inclusive, and a range may wrap the year
 * (startMonth > endMonth, e.g. Nov -> Feb).
 */

function parseDate(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

function monthOf(dateStr) {
  // 1-based month to match the registry config.
  return parseDate(dateStr).getMonth() + 1;
}

/** Inclusive month-range test that supports ranges wrapping the year boundary. */
function monthInRange(month, startMonth, endMonth) {
  if (startMonth <= endMonth) {
    return month >= startMonth && month <= endMonth;
  }
  // Wrapping range, e.g. Nov(11) -> Feb(2): in range if >= start OR <= end.
  return month >= startMonth || month <= endMonth;
}

/** Whether a YYYY-MM-DD date falls within the outing's peak season. */
export function isInPeakSeason(dateStr, peakSeason) {
  if (!peakSeason || !dateStr) return false;
  const { startMonth, endMonth } = peakSeason;
  if (!startMonth || !endMonth) return false;
  return monthInRange(monthOf(dateStr), startMonth, endMonth);
}

/**
 * Expand a peak-season window by `pad` months on each side ("shoulder season").
 * Returns a new { startMonth, endMonth }. Once the padded span covers >= 12
 * months it effectively means year-round.
 */
function expandSeason(peakSeason, pad) {
  const wrap = (m) => ((((m - 1) % 12) + 12) % 12) + 1; // keep within 1..12
  return {
    startMonth: wrap(peakSeason.startMonth - pad),
    endMonth: wrap(peakSeason.endMonth + pad),
  };
}

function byDateAsc([a], [b]) {
  return parseDate(a) - parseDate(b);
}

/**
 * Pick the dated cancellation entries to display.
 *
 * @param {Record<string, number>} dateCounts - map of YYYY-MM-DD -> spot count
 * @param {{startMonth:number,endMonth:number,label?:string}|null} peakSeason
 * @param {number} maxDates
 * @returns {{ dates: Array<[string, number]>, mode: 'peak'|'shoulder'|'upcoming'|'recent' }}
 *   `dates` is sorted ascending by date; `mode` tells the UI how to label the
 *   section honestly. When `peakSeason` is null, behaves like the prior
 *   upcoming/recent logic.
 */
export function selectPeakSeasonDates(dateCounts, peakSeason, maxDates = 12) {
  const entries = Object.entries(dateCounts || {});
  if (entries.length === 0) {
    return { dates: [], mode: peakSeason ? 'peak' : 'upcoming' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = entries
    .filter(([dateStr]) => parseDate(dateStr) >= today)
    .sort(byDateAsc);

  if (peakSeason?.startMonth && peakSeason?.endMonth) {
    // 1) Strict peak-season window, upcoming first.
    const strict = upcoming.filter(([dateStr]) =>
      isInPeakSeason(dateStr, peakSeason)
    );
    if (strict.length > 0) {
      return { dates: strict.slice(0, maxDates), mode: 'peak' };
    }

    // 2) Shoulder season: widen by one month on each side.
    const shoulder = expandSeason(peakSeason, 1);
    const shoulderDates = upcoming.filter(([dateStr]) =>
      isInPeakSeason(dateStr, shoulder)
    );
    if (shoulderDates.length > 0) {
      return { dates: shoulderDates.slice(0, maxDates), mode: 'shoulder' };
    }
  }

  // 3) Any upcoming caught dates (year-round outings land here too).
  if (upcoming.length > 0) {
    return { dates: upcoming.slice(0, maxDates), mode: 'upcoming' };
  }

  // 4) Nothing upcoming: most recent caught dates (preserves prior behavior).
  const recent = entries.sort(byDateAsc).slice(-maxDates);
  return { dates: recent, mode: 'recent' };
}

/** Human-readable peak-season range, e.g. "Mar - Oct". */
export function peakSeasonRangeLabel(peakSeason) {
  if (!peakSeason) return null;
  if (peakSeason.label) return peakSeason.label;
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const { startMonth, endMonth } = peakSeason;
  if (!startMonth || !endMonth) return null;
  return `${MONTHS[startMonth - 1]} - ${MONTHS[endMonth - 1]}`;
}
